import { Injectable, Injector } from '@angular/core';
import { User } from '@authority-labs/api-interfaces';
import { Path } from '@authority-labs/enums';
import { ChangeType } from '@check-list/core/enums';
import { TranslocoService } from '@ngneat/transloco';
import { plainToInstance } from 'class-transformer';
import { Observable } from 'rxjs';
import { filter, tap } from 'rxjs/operators';
import { ColumnFilter } from '../../classes/models/column-filter.model';
import { CrudService } from '../../shared/services/crud/crud.service';

@Injectable({
  providedIn: 'root'
})
export class UsersService extends CrudService<User> {
  users: User[];
  usersFilters: ColumnFilter[] = [];

  constructor(injector: Injector, private translocoService: TranslocoService) {
    super(injector, Path.Users, User);

    UsersService.changesEmitter
      .pipe(filter((event) => event.path === Path.Users))
      .subscribe((event) => {
        if (
          [ChangeType.deleted, ChangeType.edited, ChangeType.created].includes(
            event.action
          )
        ) {
          this.getAll().subscribe();
        }
      });
  }

  getAll(): Observable<User[]> {
    return super.getAll().pipe(
      tap((users) => {
        this.users = users;
        this.setUsersFilter(users);
      })
    );
  }

  disableUser(userId: number) {
    return this.http.delete<void>(`${this.api}/${userId}/access`);
  }

  getUploadAvatarUrl(userId: number): string {
    return `${this.api}/${userId}/upload`;
  }

  isManager(username: string): boolean {
    const role = this.getUserRole(username);
    return role ? ['super-admin', 'admin'].includes(role) : false;
  }

  getDisplayName(username: string): string {
    const user = this.users.find((u) => u.username === username);
    return user?.displayName;
  }

  isTester(username: string): boolean {
    const role = this.getUserRole(username);
    return role ? ['senior-tester', 'tester'].includes(role) : false;
  }

  getUserRole(username: string): string {
    const user = this.users.find((u) => u.username === username);
    return user?.role;
  }

  getTesterUsers(): User[] {
    return this.users.filter((user) =>
      ['super-admin', 'senior-tester', 'tester'].includes(user.role)
    );
  }

  getTesters(): ColumnFilter[] {
    return this.getTesterUsers().map((item) => ({
      label: item.displayName,
      value: item.username
    }));
  }

  getManagers(): User[] {
    return this.users.filter((user) =>
      ['super-admin', 'admin'].includes(user.role)
    );
  }

  getApprovers(): User[] {
    return this.users.filter((user) => user.canApproveInspections);
  }

  private setUsersFilter(users: User[] = []): void {
    this.usersFilters = users.map((item) => {
      this.translocoService.setTranslationKey(
        `users.${item.username}`,
        item.displayName
      );
      return {
        label: item.displayName,
        value: item.username
      };
    });
  }

  manyPlainToClass(users: User[]): User[] {
    users = users.map((user) => ({ ...user, name: user.displayName }));
    return plainToInstance(User, users);
  }

  plainToClass = (user: User): User =>
    plainToInstance(User, { ...user, name: user.displayName });
}
